import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { NavBar } from '../components/NavBar';
interface BlogPost {
  title: string;
  description: string;
  slug: string;
  emoji: string;
  publishDate: string;
  readTime: string;
  category: string;
  datePublished: string;
}
const blogPosts: BlogPost[] = [{
  title: '7 Signs of Gaslighting in Texts | AI Detection',
  description: 'Discover 7 signs of gaslighting our AI catches instantly. Upload your texts to detect manipulation, denial, and emotional abuse patterns in seconds.',
  slug: '7-signs-of-gaslighting',
  emoji: '💀',
  publishDate: 'January 14, 2025',
  datePublished: '2025-01-14',
  readTime: '5 min read',
  category: 'Gaslighting'
}, {
  title: 'Is Your Ex a Covert Narcissist? AI Analysis',
  description: 'Discover if your ex is a covert narcissist. Our AI analyzes texts to detect manipulation, gaslighting & hidden control.',
  slug: 'is-your-ex-covert-narcissist',
  emoji: '🎭',
  publishDate: 'March 8, 2025',
  datePublished: '2025-03-08',
  readTime: '6 min read',
  category: 'Narcissism'
}, {
  title: 'Passive-Aggressive Texts: Complete Guide',
  description: 'Learn to spot passive-aggressive texts with our AI guide. Detect sarcasm, backhanded compliments & manipulation.',
  slug: 'how-to-spot-passive-aggressive-texts',
  emoji: '😤',
  publishDate: 'May 22, 2025',
  datePublished: '2025-05-22',
  readTime: '7 min read',
  category: 'Communication'
}, {
  title: 'Narcissism & Gaslighting: Signs Detected',
  description: 'Learn about narcissism and gaslighting. Understand manipulation tactics and how AI detects toxic patterns in texts.',
  slug: 'narcissism-and-gaslighting-explained',
  emoji: '💀',
  publishDate: 'July 15, 2025',
  datePublished: '2025-07-15',
  readTime: '5 min read',
  category: 'Education'
}, {
  title: 'Emotional Drain in Texts: AI Breakdown',
  description: 'Discover why some people make you feel drained. AI analyzes emotional patterns in texts to reveal one-sided relationships.',
  slug: 'why-some-people-drain-you',
  emoji: '😮‍💨',
  publishDate: 'September 3, 2025',
  datePublished: '2025-09-03',
  readTime: '8 min read',
  category: 'Emotional Health'
}, {
  title: '7 Texting Patterns That Drain You | Part 2',
  description: 'More subtle patterns that drain your energy. AI detects over-explaining, one-uppers, emoji confusion & more.',
  slug: 'emotional-drain-part-2',
  emoji: '😭',
  publishDate: 'November 16, 2025',
  datePublished: '2025-11-16',
  readTime: '7 min read',
  category: 'Emotional Health'
}];
// Group posts by category
const categories = ['Gaslighting', 'Narcissism', 'Communication', 'Education', 'Emotional Health'];
export function BlogIndexPage() {
  // JSON-LD Schema for Blog Listing
  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    headline: 'ToxicDetector Blog',
    description: 'Expert insights on toxic relationships, gaslighting, narcissism, and emotional manipulation. Learn how to spot red flags with AI relationship analysis.',
    url: 'https://toxicdetector.com/blog',
    publisher: {
      '@type': 'Organization',
      name: 'ToxicDetector',
      url: 'https://toxicdetector.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://toxicdetector.com/images/logo.svg'
      }
    },
    blogPost: blogPosts.map(post => ({
      '@type': 'BlogPosting',
      headline: post.title,
      image: `https://toxicdetector.com/images/blog/${post.slug}.svg`,
      url: `https://toxicdetector.com/blog/${post.slug}`,
      datePublished: post.datePublished,
      description: post.description
    }))
  };
  return <>
      <Helmet data-id="element-3843">
        {/* Primary Meta Tags */}
        <title data-id="element-3844">
          ToxicDetector Blog: Relationship Insights, Gaslighting Guides & Toxic
          Pattern Analysis
        </title>
        <meta name="description" content="Expert insights on toxic relationships, gaslighting, narcissism, and emotional manipulation. Learn how to spot red flags using AI-powered relationship analysis." data-id="element-3845" />
        <meta name="keywords" content="toxic relationship insights, gaslighting signs, narcissist red flags, AI relationship analysis blog, toxic patterns education, emotional manipulation guides" data-id="element-3846" />
        <link rel="canonical" href="https://toxicdetector.com/blog" data-id="element-3847" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="ToxicDetector Blog: Relationship Insights & Toxic Pattern Guides" data-id="element-3848" />
        <meta property="og:description" content="Expert guides on gaslighting, manipulation & narcissism. Learn how AI detects toxic patterns through real text conversations." data-id="element-3849" />
        <meta property="og:url" content="https://toxicdetector.com/blog" data-id="element-3850" />
        <meta property="og:type" content="website" data-id="element-3851" />
        <meta property="og:image" content="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" data-id="element-3852" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-3853" />
        <meta name="twitter:title" content="ToxicDetector Blog: Insights Into Gaslighting & Toxic Patterns" data-id="element-3854" />
        <meta name="twitter:description" content="Learn how to spot manipulation, emotional abuse & toxic relationship patterns with AI-powered insights." data-id="element-3855" />
        <meta name="twitter:image" content="https://cdn.magicpatterns.com/uploads/6fWHFbe1LodeRZcxwg7PYR/file_00000000970c722f86dc01c42d5e2c9f_(1).png" data-id="element-3856" />

        {/* JSON-LD Schema */}
        <script type="application/ld+json" data-id="element-3857">{JSON.stringify(blogSchema)}</script>
      </Helmet>

      <div className="flex flex-col w-full min-h-screen bg-stone-50" data-id="element-3858">
        <NavBar data-id="element-3859" />
        <header className="bg-white border-b border-gray-200" data-id="element-3860">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center" data-id="element-3861">
            <div className="flex items-center justify-between" data-id="element-3862">
              <Link to="/" className="text-2xl font-bold tracking-tight text-gray-900 hover:text-gray-700 transition-colors" data-id="element-3863">
                🚩 ToxicDetector
              </Link>
              <Link to="/signup" className="bg-purple-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-purple-700 transition-colors text-sm" data-id="element-3864">
                Try Free Scan
              </Link>
            </div>
          </div>
        </header>

        {/* Main Content - Narrower, more focused */}
        <main className="max-w-3xl mx-auto w-full px-6 py-12" data-id="element-3865">
          {/* Back Link */}
          <Link to="/" className="inline-flex items-center text-gray-600 text-sm mb-10 hover:text-gray-900 transition-colors group" data-id="element-3866">
            <ArrowLeft size={16} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-3867" />
            Back to Home
          </Link>

          {/* Hero Section - More editorial, less corporate */}
          <div className="mb-16" data-id="element-3868">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight text-gray-900" data-id="element-3869">
              Relationship Insights & Guides
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed" data-id="element-3870">
              Expert insights on toxic relationships, gaslighting, narcissism,
              and emotional manipulation. Learn to spot red flags and protect
              your mental health.
            </p>
          </div>

          {/* Blog Posts by Category */}
          {categories.map(category => {
          const categoryPosts = blogPosts.filter(post => post.category === category);
          if (categoryPosts.length === 0) return null;
          return <section key={category} className="mb-16" data-id="element-3871">
                <h2 className="text-2xl font-bold mb-6 text-gray-900" data-id="element-3872">
                  {category}
                </h2>
                <div className="space-y-6" data-id="element-3873">
                  {categoryPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-xl border border-gray-200 p-6 hover:border-purple-200 hover:shadow-md transition-all duration-200" data-id="element-3874">
                      <div className="flex items-start gap-4" data-id="element-3875">
                        {/* Emoji */}
                        <span className="text-3xl flex-shrink-0" data-id="element-3876">
                          {post.emoji}
                        </span>
                        <div className="flex-1 min-w-0" data-id="element-3877">
                          {/* Category Badge */}
                          <span className="inline-block text-xs font-medium text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full mb-3" data-id="element-3878">
                            {post.category}
                          </span>
                          {/* Title */}
                          <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-purple-600 transition-colors leading-snug" data-id="element-3879">
                            {post.title}
                          </h3>
                          {/* Description */}
                          <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2" data-id="element-3880">
                            {post.description}
                          </p>
                          {/* Meta Info */}
                          <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-3881">
                            <div className="flex items-center gap-1.5" data-id="element-3882">
                              <Calendar size={14} data-id="element-3883" />
                              <span data-id="element-3884">{post.publishDate}</span>
                            </div>
                            <div className="flex items-center gap-1.5" data-id="element-3885">
                              <Clock size={14} data-id="element-3886" />
                              <span data-id="element-3887">{post.readTime}</span>
                            </div>
                          </div>
                        </div>
                        {/* Arrow */}
                        <ArrowRight size={20} className="flex-shrink-0 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all mt-1" data-id="element-3888" />
                      </div>
                    </Link>)}
                </div>
              </section>;
        })}

          {/* CTA Section - Simplified to match landing page */}
          <section className="bg-purple-600 rounded-2xl p-10 text-center text-white mt-16" data-id="element-3889">
            <h2 className="text-3xl font-bold mb-4 tracking-tight" data-id="element-3890">
              Ready to Analyze Your Conversations?
            </h2>
            <p className="text-purple-100 mb-8 text-lg leading-relaxed max-w-xl mx-auto" data-id="element-3891">
              Upload your chat screenshots and get instant AI-powered insights
              into toxic patterns, manipulation, and red flags.
            </p>
            <Link to="/signup" className="inline-flex items-center gap-2 bg-white text-purple-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors" data-id="element-3892">
              Start Free Scan
              <ArrowRight size={18} data-id="element-3893" />
            </Link>
          </section>
        </main>

        {/* Footer - Simplified to match landing page */}
        <footer className="bg-white border-t border-gray-200 py-12 mt-16" data-id="element-3894">
          <div className="max-w-3xl mx-auto px-6" data-id="element-3895">
            <div className="grid md:grid-cols-3 gap-8 mb-8" data-id="element-3896">
              <div data-id="element-3897">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3898">Product</h3>
                <ul className="space-y-2 text-sm" data-id="element-3899">
                  <li data-id="element-3900">
                    <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3901">
                      Home
                    </Link>
                  </li>
                  <li data-id="element-3902">
                    <Link to="/signup" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3903">
                      Sign Up
                    </Link>
                  </li>
                  <li data-id="element-3904">
                    <Link to="/login" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3905">
                      Login
                    </Link>
                  </li>
                </ul>
              </div>
              <div data-id="element-3906">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3907">Resources</h3>
                <ul className="space-y-2 text-sm" data-id="element-3908">
                  <li data-id="element-3909">
                    <a href="/blog" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3910">
                      Blog
                    </a>
                  </li>
                  <li data-id="element-3911">
                    <a href="/privacy" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3912">
                      Privacy Policy
                    </a>
                  </li>
                  <li data-id="element-3913">
                    <a href="/terms" className="text-gray-600 hover:text-gray-900 transition-colors" data-id="element-3914">
                      Terms of Service
                    </a>
                  </li>
                </ul>
              </div>
              <div data-id="element-3915">
                <h3 className="font-bold mb-3 text-gray-900" data-id="element-3916">About</h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-3917">
                  AI-powered relationship analysis to help you spot toxic
                  patterns and protect your mental health.
                </p>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-8 text-center" data-id="element-3918">
              <p className="text-gray-500 text-sm" data-id="element-3919">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>;
}